import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { PageActions } from '../../app/shell/PageActions';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { MapPinIcon } from '@phosphor-icons/react/dist/csr/MapPin';

import { CentreFormDialog } from './CentreFormDialog';
import { ReferenceLifecycleActions } from './ReferenceLifecycleActions';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  ClientDataTable,
  type TableFilter,
} from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { ApiError } from '../../lib/api/client';
import {
  centresApi,
  citiesApi,
  dispensariesApi,
  type Centre,
  type City,
  type Dispensary,
  type ReferenceParentRef,
} from '../../lib/api/reference';
import styles from './ReferenceList.module.css';

/**
 * The centre list — the bottom of the reference hierarchy. See
 * `DispensaryList.tsx`'s docblock for the shared reasoning.
 *
 * A DISPENSARY COLUMN AND A DISPENSARY FILTER. The product owner: "not
 * seeing anywhere which dispensary is related to which centre". Since cities
 * became global (the API's `make_cities_global` migration) a centre's city
 * no longer implies its dispensary either, so the owning dispensary is shown
 * on every row. Both parent columns render from the row ITSELF —
 * `CentreResource` now carries `dispensary`/`city` with both names — never
 * from a lookup into a separately-fetched catalogue, so a row whose parent
 * is inactive (absent from an active-only catalogue) still names it.
 *
 * The two catalogues ARE still fetched, active only, for the two things the
 * rows cannot supply: the filter facets' option lists and the create/edit
 * form's pickers (`CentreFormDialog` asks for a dispensary AND a city).
 */

export interface CentreListProps {
  permissions: readonly string[];
}

interface CentreRow extends Row, Centre {}

function toRow(node: Centre): CentreRow {
  return { ...node, id: node.uuid };
}

export function CentreList({ permissions }: CentreListProps) {
  const { t, i18n } = useTranslation();
  const canManage = permissions.includes('reference.manage');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly CentreRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [cities, setCities] = useState<readonly City[]>([]);
  const [dispensaries, setDispensaries] = useState<readonly Dispensary[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<Centre | undefined>(undefined);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const list = await centresApi.list(true);
      const mapped = list.map(toRow);
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The two parent catalogues, for the filters and the form's pickers.
  // Reading them needs no `reference.manage`; an `ApiError` degrades a
  // facet/picker to empty rather than surfacing (the rows themselves carry
  // their parents' names, so no column depends on these), and a programming
  // fault is not swallowed alongside it. Fetched independently: one failing
  // must not cost the other.
  useEffect(() => {
    let cancelled = false;

    async function loadCities(): Promise<void> {
      try {
        const list = await citiesApi.list(false);
        if (!cancelled) setCities(list);
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
      }
    }

    async function loadDispensaries(): Promise<void> {
      try {
        const list = await dispensariesApi.list(false);
        if (!cancelled) setDispensaries(list);
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
      }
    }

    void loadCities();
    void loadDispensaries();
    return () => {
      cancelled = true;
    };
  }, []);

  function nodeLabel(node: Centre): string {
    return i18n.language.startsWith('ar') ? node.name_ar : node.name_en;
  }

  function parentName(parent: ReferenceParentRef | null): string | null {
    if (parent === null) return null;
    return i18n.language.startsWith('ar') ? parent.name_ar : parent.name_en;
  }

  function applyRowChange(uuid: string, changes: Partial<Centre>): void {
    setRows((current) => current.map((row) => (row.id === uuid ? { ...row, ...changes } : row)));
  }

  function applyRowDelete(uuid: string): void {
    setRows((current) => current.filter((row) => row.id !== uuid));
  }

  function openCreateForm(): void {
    setEditingNode(undefined);
    setFormOpen(true);
  }

  function openEditForm(node: Centre): void {
    setEditingNode(node);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
  }

  function handleSaved(saved: Centre): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        ? current.map((row) => (row.id === saved.uuid ? { ...row, ...saved } : row))
        : [...current, toRow(saved)];
    });
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function renderName(row: Row): ReactNode {
    return nodeLabel(row as unknown as Centre);
  }

  function renderDispensary(row: Row): ReactNode {
    return parentName((row as unknown as Centre).dispensary);
  }

  function renderCity(row: Row): ReactNode {
    return parentName((row as unknown as Centre).city);
  }

  function renderStatus(row: Row): ReactNode {
    return (row as unknown as Centre).is_active
      ? t('reference.statusActive')
      : t('reference.statusInactive');
  }

  /**
   * The map link. A centre's `google_map_url` was already on the record and
   * already editable in the form, and was reachable from nowhere — a driver
   * looking for the branch had to open the edit dialog for a URL they then
   * had to copy by hand.
   *
   * `target="_blank"` with `rel="noopener noreferrer"`: this is a link to
   * somebody else's origin, and the reader is in the middle of a table they
   * have not finished with. `noopener` is what keeps that origin from
   * reaching back through `window.opener`.
   *
   * The accessible name carries the CENTRE'S NAME, not "Open map" — a screen
   * reader listing the links on this page would otherwise read the same two
   * words once per row, with nothing to tell them apart.
   */
  function renderMap(row: Row): ReactNode {
    const node = row as unknown as Centre;
    if (node.google_map_url === null || node.google_map_url.trim() === '') return null;
    return (
      <a
        className={styles['mapLink']}
        href={node.google_map_url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <MapPinIcon size={16} aria-hidden="true" />
        <span>{t('reference.openMap', { name: nodeLabel(node) })}</span>
      </a>
    );
  }

  function renderActions(row: Row): ReactNode {
    const node = row as unknown as Centre;
    return (
      <ReferenceLifecycleActions
        node={node}
        label={nodeLabel(node)}
        canManage={canManage}
        onEdit={openEditForm}
        onChanged={applyRowChange}
        onDeleted={applyRowDelete}
        activate={centresApi.activate}
        deactivate={centresApi.deactivate}
        remove={centresApi.remove}
      />
    );
  }

  /**
   * The status facet. `is_active` is a BOOLEAN on the row and the Status
   * column renders it as a word, so neither the search box nor a sort on
   * that column could ever answer "show me the inactive ones" — the word
   * the reader would search for is not on the row at all.
   */
  const filters: readonly TableFilter[] = [
    {
      id: 'status',
      label: t('reference.columnStatus'),
      key: 'is_active',
      options: [
        { value: 'true', label: t('reference.statusActive') },
        { value: 'false', label: t('reference.statusInactive') },
      ],
    },
    {
      id: 'dispensary',
      label: t('reference.columnDispensary'),
      key: 'dispensary_uuid',
      options: dispensaries.map((node) => ({
        value: node.uuid,
        label: i18n.language.startsWith('ar') ? node.name_ar : node.name_en,
      })),
    },
    {
      id: 'city',
      label: t('reference.columnCity'),
      key: 'city_uuid',
      options: cities.map((node) => ({
        value: node.uuid,
        label: i18n.language.startsWith('ar') ? node.name_ar : node.name_en,
      })),
    },
  ];

  const columns: readonly Column[] = [
    { key: 'name_en', label: t('reference.columnName'), render: renderName },
    { key: 'external_code', label: t('reference.columnCode'), neverMirrors: true },
    { key: 'dispensary_uuid', label: t('reference.columnDispensary'), render: renderDispensary },
    { key: 'city_uuid', label: t('reference.columnCity'), render: renderCity },
    { key: 'status', label: t('reference.columnStatus'), render: renderStatus },
    { key: 'google_map_url', label: t('reference.columnLocation'), render: renderMap },
    { key: 'actions', label: t('reference.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {/* No sub-title under the shell's <h1>; the tab's one command rides in
          the title row beside the level switch. */}
      {canManage ? (
        <PageActions>
          <Button onClick={openCreateForm} icon={<PlusIcon size={16} />}>
            {t('reference.addCentre')}
          </Button>
        </PageActions>
      ) : null}

      <ClientDataTable
        caption={t('reference.tabCentres')}
        columns={columns}
        rows={rows}
        filters={filters}
        status={status}
        empty={{ body: t('reference.emptyCentres') }}
        error={{ title: t('reference.loadFailedCentres') }}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('reference.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="centreListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <CentreFormDialog
        open={formOpen}
        node={editingNode}
        dispensaries={dispensaries.filter((d) => d.is_active)}
        cities={cities.filter((c) => c.is_active)}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    </div>
  );
}
